import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAPIContext } from './api';
import { useAuthContext } from './auth';
import { BrandsAPI } from '../../../constructs/brands/interface';
import { Banner } from '../types/banner';

import { sortBanners } from '../utils';

const ENDPOINT = '/banners';

interface BannersContextType {
  allBanners: Banner[];
  isRefreshingAll: boolean;
  folders: string[];

  getBannersByFolder: (folderPath?: string) => Promise<Banner[]>;
  refreshAllBanners: () => Promise<void>;
  loadFolders: () => Promise<string[]>;
}

const BannersContext = createContext<BannersContextType | undefined>(undefined);

export const BannersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { callEndpoint } = useAPIContext();
  const { session } = useAuthContext();

  const hasInitialized = useRef(false);

  const [allBanners, setAllBanners] = useState<Banner[]>([]);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [folders, setFolders] = useState<string[]>([]);

  const loadFolders = useCallback(async (): Promise<string[]> => {
    if (!session) return [];

    try {
      const result = await callEndpoint<BrandsAPI, 'list-folders'>('brands', '/folders', 'GET');
      setFolders(result);
      return result;
    } catch (error) {
      return [];
    }
  }, [session, callEndpoint]);

  const getBannersByFolder = useMemo(() => {
    return async (folderPath?: string): Promise<Banner[]> => {
      if (!session) return [];

      try {
        const endpoint = folderPath ? `${ENDPOINT}/${folderPath}` : ENDPOINT;
        const res = await callEndpoint<BrandsAPI, 'list-banners'>('brands', endpoint, 'GET');

        return sortBanners(res);
      } catch (error) {
        return [];
      }
    };
  }, [callEndpoint, session]);

  const refreshAllBanners = useCallback(async () => {
    if (!session) return;

    setIsRefreshingAll(true);
    try {
      const endpoint = ENDPOINT;
      const res = await callEndpoint<BrandsAPI, 'list-banners'>('brands', endpoint, 'GET');
      setAllBanners(sortBanners(res));
    } finally {
      setIsRefreshingAll(false);
    }
  }, [session, callEndpoint]);

  useEffect(() => {
    if (session && !hasInitialized.current) {
      hasInitialized.current = true;
      refreshAllBanners();
      loadFolders();
    }

    // Si no hay sesión, resetear el flag
    if (!session) {
      hasInitialized.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const value: BannersContextType = {
    allBanners,
    folders,
    isRefreshingAll,
    getBannersByFolder,
    loadFolders,
    refreshAllBanners
  };

  return <BannersContext.Provider value={value}>{children}</BannersContext.Provider>;
};

export const useBanners = (): BannersContextType => {
  const context = useContext(BannersContext);
  if (context === undefined) {
    throw new Error('useBanners must be used within a BannersProvider');
  }
  return context;
};
